import { payableVerdict } from "../analysis/payable-verdict.js"
import type { ApiPayable } from "../api/schemas/payables.js"
import type { BillsAgentConfig } from "./schemas.js"
import type {
  AgentGateEvidence,
  BillsGateName,
  BillsVerdict,
} from "./vocabulary.js"

/**
 * The bills policy: one pure decision over evidence the product already
 * computed.
 *
 * The facts arrive as the same payable entry the queue renders, so the agent
 * and the screen can never disagree about what a bill's matches say. The
 * evaluator adds nothing of its own: it reads the checks, the matches and the
 * two workspace facts (vendor history, duplicates) and either approves or
 * holds with every gate's evidence attached.
 *
 * When the agent is not sure, it holds the bill and says why. Every failed
 * gate label is a complete sentence in flag-title grammar, because those
 * labels are the chips a person reads in the needs-you lane.
 */

export type BillsAgentFacts = {
  /** The queue's own entry for this bill: totals, flags, matches, evidence. */
  readonly payable: ApiPayable
  /** The vendor already has an approved bill in this workspace. */
  readonly vendorHasApprovedBill: boolean
  /** Other live invoices in this bill's duplicate cluster. */
  readonly duplicateDocumentIds: readonly string[]
}

/** "$2,500.00" for USD, "2,500.00 EUR" for everything else. */
const money = (minorUnits: number, currency: string): string => {
  const formatted = (minorUnits / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return currency === "USD" ? `$${formatted}` : `${formatted} ${currency}`
}

const gateEvidence = (
  gate: BillsGateName,
  outcome: AgentGateEvidence["outcome"],
  label: string,
  expected: string | null,
  actual: string | null
): AgentGateEvidence => ({ gate, outcome, label, expected, actual })

const checksGate = (payable: ApiPayable): AgentGateEvidence => {
  if (payable.totalCents === null) {
    return gateEvidence(
      "checks",
      "failed",
      "LedgerBox cannot verify the total.",
      "a total on the bill",
      "no total"
    )
  }
  // `blockedByFlags` counts only the flags a person must close before
  // approval, which is the same subset the statements agent gates on. The two
  // agents say it the same way because it is the same fact.
  if (payable.blockedByFlags) {
    return gateEvidence(
      "checks",
      "failed",
      "LedgerBox flagged this bill in review.",
      "no required flags open",
      "required flags open"
    )
  }

  return gateEvidence(
    "checks",
    "passed",
    "Every check passed and no required flags are open.",
    "no required flags open",
    "none"
  )
}

const knownVendorGate = (
  facts: BillsAgentFacts,
  config: BillsAgentConfig
): AgentGateEvidence => {
  if (!config.knownVendorsOnly) {
    return gateEvidence(
      "known_vendor",
      "passed",
      "Known vendors only is off in your policy.",
      null,
      null
    )
  }
  if (facts.vendorHasApprovedBill) {
    return gateEvidence(
      "known_vendor",
      "passed",
      "This vendor has an approved bill.",
      "an approved bill from this vendor",
      "found"
    )
  }

  return gateEvidence(
    "known_vendor",
    "failed",
    "This is the first bill from this vendor.",
    "an approved bill from this vendor",
    "none"
  )
}

const approvalLimitGate = (
  payable: ApiPayable,
  config: BillsAgentConfig
): AgentGateEvidence => {
  const limit = money(config.approveUpToMinor, config.currency)

  if (payable.totalCents === null) {
    // The checks gate already holds the bill; this gate had nothing to read.
    return gateEvidence(
      "approval_limit",
      "not_available",
      "LedgerBox cannot verify the total.",
      limit,
      null
    )
  }

  const billCurrency =
    payable.currency === null ? null : payable.currency.trim().toUpperCase()

  if (billCurrency !== null && billCurrency !== config.currency) {
    return gateEvidence(
      "approval_limit",
      "failed",
      "This bill is not in your policy currency.",
      config.currency,
      billCurrency
    )
  }
  if (payable.totalCents > config.approveUpToMinor) {
    return gateEvidence(
      "approval_limit",
      "failed",
      `This bill is over your ${limit} limit.`,
      limit,
      money(payable.totalCents, config.currency)
    )
  }

  return gateEvidence(
    "approval_limit",
    "passed",
    "The total is inside your approval limit.",
    limit,
    money(payable.totalCents, config.currency)
  )
}

const duplicateGate = (facts: BillsAgentFacts): AgentGateEvidence => {
  if (facts.duplicateDocumentIds.length > 0) {
    const count = facts.duplicateDocumentIds.length

    return gateEvidence(
      "duplicate",
      "failed",
      "A bill with this vendor and total already exists.",
      "no duplicate candidates",
      count === 1 ? "1 duplicate candidate" : `${count} duplicate candidates`
    )
  }

  return gateEvidence(
    "duplicate",
    "passed",
    "No duplicate candidate exists.",
    "no duplicate candidates",
    "none"
  )
}

const matchingGate = (payable: ApiPayable): AgentGateEvidence => {
  if (payable.po.kind === "skipped" && payable.payment.kind === "skipped") {
    return gateEvidence(
      "matching",
      "passed",
      "Matching is off in your workspace settings.",
      null,
      null
    )
  }

  // The queue's exceptions: a check that failed, with the labels it failed on.
  if (payable.failedChecks.length > 0) {
    return gateEvidence(
      "matching",
      "failed",
      "The order or payment does not agree.",
      "every matching check passes",
      payable.failedChecks.map((entry) => entry.label).join("; ")
    )
  }

  // The queue's needs-a-person states: nothing failed, but the machine found
  // several answers or none and refuses to pick. The verdict sentence is the
  // one the queue already shows, so the chip and the row read the same.
  const verdict = payableVerdict(payable)

  if (verdict.needsPerson) {
    return gateEvidence(
      "matching",
      "failed",
      `${verdict.text}.`,
      "one order and one payment, or none needed",
      verdict.text
    )
  }

  return gateEvidence(
    "matching",
    "passed",
    "The order and payment checks pass.",
    "every matching check passes",
    verdict.text
  )
}

/**
 * `(facts, config) -> verdict`. Approves only when every gate passes; holds
 * with the failed gates' sentences otherwise. `reasons` always carries all
 * five gates, passed ones included, because "all 5 gates passed" has to be
 * able to show its work.
 */
export const evaluateBillsPolicy = (
  facts: BillsAgentFacts,
  config: BillsAgentConfig
): BillsVerdict => {
  const reasons: readonly AgentGateEvidence[] = [
    checksGate(facts.payable),
    knownVendorGate(facts, config),
    approvalLimitGate(facts.payable, config),
    duplicateGate(facts),
    matchingGate(facts.payable),
  ]

  return {
    action: reasons.some((entry) => entry.outcome === "failed")
      ? "hold"
      : "approve",
    reasons,
  }
}

/**
 * The verdict for an invoice the queue could not read at all (no total, so
 * it never entered matching). Held on the checks gate; every other gate had
 * nothing to read.
 */
export const evaluateUnreadableBill = (): BillsVerdict => ({
  action: "hold",
  reasons: [
    gateEvidence(
      "checks",
      "failed",
      "LedgerBox cannot verify the total.",
      "a total on the bill",
      "no total"
    ),
    gateEvidence("known_vendor", "not_available", "This check did not run.", null, null),
    gateEvidence("approval_limit", "not_available", "This check did not run.", null, null),
    gateEvidence("duplicate", "not_available", "This check did not run.", null, null),
    gateEvidence("matching", "not_available", "This check did not run.", null, null),
  ],
})
